/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import useStore from '@store/index';

// Types
import {
	TSelectOption,
	TSortColumn,
} from '@global-types/index';

// Hooks
import { useElementSize } from '@mantine/hooks';
import dayjs from 'dayjs';
import { TParsedUser } from '../types';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

type ObjWithStartDate = {
	startDate: {
		$gte: string;
		$lt: string;
	};
};

/**
 * Custom hook for managing conversion factor list functionality.
 * @returns {Array} An array containing references and callback functions.
 */
export const useUsers = () => {
	const [queryParamsUsers, setQueryParamsUsers] =
		useState({ entity: [] });

	const [queryParamsProjects, setQueryParamsProjects] =
		useState({});

	const [resetFilterUsers, setResetFilterUsers] =
		useState(false);

	const [resetFilterProjects, setResetFilterProjects] =
		useState(false);

	const [isOverflowFilters] = useState(false);

	const [openDrawer, setOpenDrawer] = useState(false);

	const [openDrawerAddUser, setOpenDrawerAddUser] =
		useState(false);

	const [
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
	] = useState(false);

	const [
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
	] = useState(false);

	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [openEditForm, setOpenEditForm] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	const {
		usersPage,
		usersPageSize,
		setUsersPage,
		usersSelectedRecords,
		setUsersSelectedRecords,
		usersOpenConfirmationModalDelete,
		setUsersOpenConfirmationModalDelete,
		usersQueryParams,
		setUsersQueryParams,
		usersSortStatus,
		setUsersSortStatus,
		setUsersSortParams,
		usersSortParams,
		setUsersPageSize,
		setUsersSelectedRow,
		usersSelectedRow,
		setIsOpenStartMint,
		setOpenDrawerUserForm,
		openDrawerUserForm,
		usersDatatableOptions,
		setUsersDatatableOptions,
	} = useStore();

	const containerRef = useRef<HTMLDivElement>(null);
	const { checkAcl, checkAccess } = useAuthorization();

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const handleSortParams = (_column: TSortColumn) => {
		let sortParams = _column.columnAccessor;
		switch (_column.columnAccessor) {
			case 'roleName':
				sortParams = 'role';
				break;
			case 'language':
				sortParams = 'locale.language';
				break;
			default:
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setUsersSortParams(sortParams);
		} else {
			setUsersSortParams('-' + sortParams);
		}
	};

	const handleStartMint = () => {
		//setUsersStartMintsActiveStep(0);
		setIsOpenStartMint(true);
	};

	const handleReset = () => {
		setUsersPageSize(10);
		setUsersQueryParams({});
		setUsersPage(1);
		setUsersSortParams('-createdAt');
		setUsersSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function hasStartDate(obj: ObjWithStartDate) {
		if (
			Object.keys(obj).length === 0 &&
			obj.constructor === Object
		) {
			return false;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte !== undefined &&
			obj.startDate.$lt !== undefined
		) {
			return true;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte === undefined &&
			obj.startDate.$lt === undefined
		) {
			return false;
		}

		return false;
	}

	const NBR_SELECTED_ROWS = usersSelectedRecords.length;

	const selectedFields = [
		'_id',
		'project.name',
		'user.name',
		'startDate',
		'quantity',
		'verified',
	];

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Pagination ****************************/

	const handlePaginationChangeSize = function (
		_size: string | number
	) {
		setUsersPageSize(Number(_size));
		setUsersPage(1);
	};

	const handleChangePagination = (
		data: number | string
	) => {
		setUsersPage(data);
	};

	/**************************** Filter Projects ****************************/

	const handleapplyprojectfilter = (
		projects: TSelectOption[]
	) => {
		setUsersSelectedRecords([]);
		setUsersPage(1);
		const queryParamsProjects = {
			project: projects.map(
				(_project: TSelectOption) => _project.value
			),
			user: [],
		};
		setQueryParamsProjects(queryParamsProjects);
		setUsersQueryParams({
			...usersQueryParams,
			...queryParamsProjects,
		});
		setResetFilterUsers(true);
	};

	const handleapplyrolefilter = (
		items: TSelectOption[]
	) => {
		setUsersSelectedRecords([]);
		setUsersPage(1);
		const queryParamsRoles = {
			role: items.map(
				(_role: TSelectOption) => _role.value
			),
		};
		setUsersQueryParams({
			...usersQueryParams,
			...queryParamsRoles,
		});
		setResetFilterUsers(true);
	};

	/**************************** Filter Date ****************************/
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleapplydatefilter = (date: any) => {
		setUsersSelectedRecords([]);
		let startDate;
		let endDate;
		if (
			dayjs(date[0].value[0]).isValid() &&
			dayjs(date[0].value[1]).isValid()
		) {
			startDate = date[0].value[0];
			endDate = date[0].value[1];
		} else {
			startDate = dayjs()
				.subtract(364, 'days')
				.toISOString();
			endDate = dayjs().toISOString();
		}

		const queryParamsDate = {
			startDate: {
				$gte: startDate,
				$lt: endDate,
			},
		};

		setUsersQueryParams({
			...usersQueryParams,
			...queryParamsDate,
		});
		setUsersPage(1);
	};
	/**************************** Filter Users ****************************/

	const handleapplyuserfilter = (
		users: TSelectOption[]
	) => {
		setUsersSelectedRecords([]);
		setUsersPage(1);
		const queryParamsUsers = {
			user: users.map(
				(_user: TSelectOption) => _user.value
			),
		};

		setUsersQueryParams({
			...usersQueryParams,
			...queryParamsUsers,
		});
	};

	/**************************** Filter Users ****************************/

	const handleapplyorganizationfilter = (
		items: TSelectOption[]
	) => {
		setUsersSelectedRecords([]);
		setUsersPage(1);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const queryParamsOrganizations: any = {
			organization: items.map(
				(_organization: TSelectOption) =>
					_organization.value
			),
		};
		setUsersQueryParams({
			...usersQueryParams,
			...queryParamsOrganizations,
		});
	};
	/**************************** Sort ****************************/

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlechangesortstatus = (data: any) => {
		handleSortParams(data);
		setUsersSortStatus(data);
	};

	const handleselectedrecordchange = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		selectedRecords: any
	) => {
		setUsersSelectedRecords(selectedRecords);
	};

	/**************************** Datatable ****************************/

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlerowclicked = (row: any) => {
		if (row?.record) {
			if (
				checkAcl(ACLS.USERS.READ) &&
				checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
			) {
				setUsersSelectedRow(row.record);
				setOpenDrawer(true);
			}
		}
	};

	const handleDeleteMultipleItems = () => {
		setOpenSelectedItemsToDeleteModal(true);
	};

	const handleResetSelectedItems = () => {
		setUsersSelectedRecords([]);
	};

	/**************************** Show more ****************************/

	const handlerowviewmore = (row: TParsedUser) => {
		setUsersSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleCloseDrawerShowMoreUser = () => {
		setOpenDrawer(false);
	};

	/**************************** Edit item ****************************/
	const handleedituser = (row: TParsedUser) => {
		setUsersSelectedRow(row);
		setOpenEditForm(true);
	};

	const handlecloseedituser = () =>
		setOpenEditForm(false);
	/**************************** Delete confirmation modal ****************************/

	const handlerowremoveitem = (row: TParsedUser) => {
		setUsersOpenConfirmationModalDelete({
			open: true,
			params: row,
		});
	};

	const handledeleteconfirmationmodalconfirm = (
		remove: any
	) => {
		setUsersOpenConfirmationModalDelete({
			open: false,
			params: {},
		});

		remove.mutate(
			String(usersOpenConfirmationModalDelete.params.id)
		);
		setOpenDrawer(false);
	};

	const handleclosedeleteconfirmationmodal = () => {
		setUsersOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};
	/*************************************** Delete multiple Items  ***************************************/

	const handleConfirmDeleteMultipleItem = (
		removeMultiple: any
	) => {
		const idsList = [...usersSelectedRecords].map(
			(_apiToken) => _apiToken.id
		);
		removeMultiple.mutate({
			ids: idsList,
		});

		setUsersSelectedRecords([]);
		setOpenSelectedItemsToDeleteModal(false);
	};

	/**************************** Delete confirmation modal ****************************/

	const handledeleteconversionfactor = () => {
		setUsersOpenConfirmationModalDelete({
			open: true,
			params: usersSelectedRow,
		});
	};

	/**************************** Drawer add conversion factor ****************************/

	const handleCloseDrawerAddUser = () => {
		setOpenDrawerUserForm(false);
	};

	/********************************************************************************************/

	return {
		/**************************** Drawer add conversion factor ****************************/
		handleCloseDrawerAddUser,
		/**************************** Delete item ****************************/
		handlerowremoveitem,
		handledeleteconfirmationmodalconfirm,
		handleclosedeleteconfirmationmodal,
		handledeleteconversionfactor,
		/**************************** Show more ****************************/
		handlerowviewmore,
		handleCloseDrawerShowMoreUser,
		/**************************** Datatable ****************************/
		handlerowclicked,
		/**************************** Select item ****************************/
		handleselectedrecordchange,
		/**************************** Sort ****************************/
		handlechangesortstatus,
		/**************************** Filters ****************************/
		handleapplyprojectfilter,
		handleapplyuserfilter,
		handleapplydatefilter,
		/**************************** Pagination ****************************/
		handleChangePagination,
		handlePaginationChangeSize,
		usersPage,
		usersPageSize,

		setUsersPage,
		usersSelectedRecords,
		setUsersSelectedRecords,
		usersOpenConfirmationModalDelete,
		setUsersOpenConfirmationModalDelete,
		usersQueryParams,
		setUsersQueryParams,
		usersSortStatus,
		setUsersSortStatus,
		setUsersSortParams,
		usersSortParams,
		setUsersPageSize,
		setUsersSelectedRow,
		usersSelectedRow,
		setIsOpenStartMint,
		queryParamsUsers,
		setQueryParamsUsers,
		queryParamsProjects,
		setQueryParamsProjects,
		handleScroll,
		resetFilterUsers,
		setResetFilterUsers,
		isOverflowFilters,
		openDrawer,
		setOpenDrawer,
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerUserForm,
		setOpenDrawerUserForm,
		handleSortParams,
		handleStartMint,
		hasStartDate,
		NBR_SELECTED_ROWS,
		selectedFields,
		ref,
		containerRef,
		resetFilterProjects,
		setResetFilterProjects,
		handleDeleteMultipleItems,
		handleResetSelectedItems,
		handleConfirmDeleteMultipleItem,
		usersDatatableOptions,
		setUsersDatatableOptions,
		handleedituser,
		openEditForm,
		setOpenEditForm,
		handlecloseedituser,
		handleapplyorganizationfilter,
		handleapplyrolefilter,
		openDrawerAddUser,
		setOpenDrawerAddUser,
	};
};
